import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useCart, useUser } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CreditCard, ArrowLeft, Lock } from "lucide-react";
import { toast } from "sonner";

export const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cart, cartTotal, clearCart } = useCart();
  const { user, login, register } = useUser();

  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [processing, setProcessing] = useState(false);

  // Auth form states
  const [authTab, setAuthTab] = useState("guest");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [registerName, setRegisterName] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    const result = await login(loginEmail, loginPassword);
    if (result.success) {
      setEmail(loginEmail);
      toast.success("Logged in successfully");
    } else {
      toast.error(result.error);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const result = await register(registerEmail, registerName, registerPassword);
    if (result.success) {
      setEmail(registerEmail);
      toast.success("Account created successfully");
    } else {
      toast.error(result.error);
    }
  };

  const handleCheckout = async () => {
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    if (cart.items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    setProcessing(true);

    try {
      // Create order
      const orderResponse = await axios.post(`${API}/orders`, {
        email,
        phone: phone || null,
        user_id: user?.id || null,
        items: cart.items,
        payment_method: paymentMethod,
      });

      const order = orderResponse.data;
      const originUrl = window.location.origin;

      // Process payment based on method
      if (paymentMethod === "stripe") {
        const paymentResponse = await axios.post(`${API}/payments/stripe/checkout`, {
          order_id: order.id,
          origin_url: originUrl,
        });
        
        // Redirect to Stripe
        window.location.href = paymentResponse.data.url;
      } else if (paymentMethod === "paypal") {
        const paymentResponse = await axios.post(`${API}/payments/paypal/create`, {
          order_id: order.id,
          origin_url: originUrl,
        });

        if (paymentResponse.data.approve_url) {
          window.location.href = paymentResponse.data.approve_url;
        } else {
          toast.error("PayPal is not configured. Please use another payment method.");
          setProcessing(false);
        }
      } else if (paymentMethod === "razorpay") {
        const paymentResponse = await axios.post(`${API}/payments/razorpay/create`, {
          order_id: order.id,
          origin_url: originUrl,
        });

        if (paymentResponse.data.razorpay_order_id) {
          // Load Razorpay script if not loaded
          if (!window.Razorpay) {
            const script = document.createElement("script");
            script.src = "https://checkout.razorpay.com/v1/checkout.js";
            document.body.appendChild(script);
            await new Promise((resolve) => (script.onload = resolve));
          }

          const options = {
            key: paymentResponse.data.key_id,
            amount: paymentResponse.data.amount,
            currency: paymentResponse.data.currency,
            order_id: paymentResponse.data.razorpay_order_id,
            name: "Kavya's Art",
            description: "Digital Artwork Purchase",
            handler: async (response) => {
              try {
                await axios.post(`${API}/payments/razorpay/verify`, {
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  order_id: order.id,
                });
                await clearCart();
                navigate(`/order-confirmation?order_id=${order.id}`);
              } catch (e) {
                toast.error("Payment verification failed");
              }
            },
            prefill: {
              email: email,
              contact: phone || "",
            },
          };

          const rzp = new window.Razorpay(options);
          rzp.open();
          setProcessing(false);
        } else {
          toast.error("Razorpay is not configured. Please use another payment method.");
          setProcessing(false);
        }
      }
    } catch (e) {
      console.error("Checkout error:", e);
      toast.error(e.response?.data?.detail || "Checkout failed. Please try again.");
      setProcessing(false);
    }
  };

  if (cart.items.length === 0) {
    return (
      <div className="py-24 px-6 text-center max-w-md mx-auto" data-testid="empty-checkout">
        <h1 className="font-serif text-3xl mb-4">Your Cart is Empty</h1>
        <p className="text-muted-foreground mb-8">Add some artwork to checkout.</p>
        <Button
          onClick={() => navigate("/shop")}
          className="rounded-none px-8 py-3 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-bold"
        >
          Browse Artwork
        </Button>
      </div>
    );
  }

  return (
    <div className="py-12 md:py-16 px-6 md:px-12 lg:px-24 max-w-screen-2xl mx-auto" data-testid="checkout-page">
      <Button
        variant="ghost"
        onClick={() => navigate("/cart")}
        className="mb-8 gap-2"
        data-testid="back-to-cart"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Cart
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Checkout Form */}
        <div>
          <h1 className="font-serif text-4xl md:text-5xl font-normal tracking-tight mb-8">Checkout</h1>

          {/* Account/Guest Tabs */}
          {!user && (
            <div className="mb-8">
              <Tabs value={authTab} onValueChange={setAuthTab}>
                <TabsList className="grid w-full grid-cols-3 rounded-none">
                  <TabsTrigger value="guest" className="rounded-none" data-testid="guest-tab">Guest</TabsTrigger>
                  <TabsTrigger value="login" className="rounded-none" data-testid="login-tab">Login</TabsTrigger>
                  <TabsTrigger value="register" className="rounded-none" data-testid="register-tab">Register</TabsTrigger>
                </TabsList>

                <TabsContent value="guest" className="mt-6">
                  <p className="text-sm text-muted-foreground mb-4">
                    Continue as guest. Your download link will be sent to your email.
                  </p>
                </TabsContent>

                <TabsContent value="login" className="mt-6">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        className="rounded-none mt-1"
                        data-testid="login-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="login-password">Password</Label>
                      <Input
                        id="login-password"
                        type="password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="rounded-none mt-1"
                        data-testid="login-password-input"
                      />
                    </div>
                    <Button type="submit" className="rounded-none w-full" data-testid="login-submit">
                      Login
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="register" className="mt-6">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div>
                      <Label htmlFor="register-name">Name</Label>
                      <Input
                        id="register-name"
                        value={registerName}
                        onChange={(e) => setRegisterName(e.target.value)}
                        className="rounded-none mt-1"
                        data-testid="register-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="register-email">Email</Label>
                      <Input
                        id="register-email"
                        type="email"
                        value={registerEmail}
                        onChange={(e) => setRegisterEmail(e.target.value)}
                        className="rounded-none mt-1"
                        data-testid="register-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="register-password">Password</Label>
                      <Input
                        id="register-password"
                        type="password"
                        value={registerPassword}
                        onChange={(e) => setRegisterPassword(e.target.value)}
                        className="rounded-none mt-1"
                        data-testid="register-password-input"
                      />
                    </div>
                    <Button type="submit" className="rounded-none w-full" data-testid="register-submit">
                      Create Account
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </div>
          )}

          {user && (
            <div className="mb-8 p-4 bg-muted/30 rounded-none">
              <p className="text-sm">Logged in as <strong>{user.email}</strong></p>
            </div>
          )}

          {/* Contact Info */}
          <div className="space-y-6 mb-8">
            <h2 className="font-serif text-xl">Contact Information</h2>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="rounded-none mt-1"
                required
                data-testid="email-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Download link will be sent here
              </p>
            </div>
            <div>
              <Label htmlFor="phone">Phone (optional)</Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 234 567 8900"
                className="rounded-none mt-1"
                data-testid="phone-input"
              />
            </div>
          </div>

          {/* Payment Method */}
          <div className="space-y-6">
            <h2 className="font-serif text-xl">Payment Method</h2>
            <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
              <div className="flex items-center space-x-3 p-4 border border-border hover:border-primary transition-colors cursor-pointer" onClick={() => setPaymentMethod("stripe")}>
                <RadioGroupItem value="stripe" id="stripe" data-testid="stripe-radio" />
                <Label htmlFor="stripe" className="flex-1 cursor-pointer">
                  <span className="font-medium">Credit/Debit Card</span>
                  <span className="block text-xs text-muted-foreground">Powered by Stripe</span>
                </Label>
                <CreditCard className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="flex items-center space-x-3 p-4 border border-border hover:border-primary transition-colors cursor-pointer" onClick={() => setPaymentMethod("paypal")}>
                <RadioGroupItem value="paypal" id="paypal" data-testid="paypal-radio" />
                <Label htmlFor="paypal" className="flex-1 cursor-pointer">
                  <span className="font-medium">PayPal</span>
                  <span className="block text-xs text-muted-foreground">Pay with your PayPal account</span>
                </Label>
                <span className="text-sm font-bold text-blue-600">PayPal</span>
              </div>
              <div className="flex items-center space-x-3 p-4 border border-border hover:border-primary transition-colors cursor-pointer" onClick={() => setPaymentMethod("razorpay")}>
                <RadioGroupItem value="razorpay" id="razorpay" data-testid="razorpay-radio" />
                <Label htmlFor="razorpay" className="flex-1 cursor-pointer">
                  <span className="font-medium">Razorpay</span>
                  <span className="block text-xs text-muted-foreground">UPI, Cards, Net Banking (India)</span>
                </Label>
                <span className="text-sm font-bold text-blue-500">Razorpay</span>
              </div>
            </RadioGroup>
          </div>
        </div>

        {/* Order Summary */}
        <div>
          <div className="bg-muted/30 p-6 md:p-8 sticky top-24">
            <h2 className="font-serif text-xl mb-6">Order Summary</h2>

            <div className="space-y-4 mb-6">
              {cart.items.map((item) => (
                <div key={item.artwork_id} className="flex gap-4">
                  <img
                    src={item.image_url}
                    alt={item.title}
                    className="w-16 h-20 object-cover"
                  />
                  <div className="flex-1">
                    <h4 className="font-medium text-sm">{item.title}</h4>
                    <p className="text-xs text-muted-foreground">Digital Download</p>
                  </div>
                  <p className="font-medium text-sm">${item.price.toFixed(2)}</p>
                </div>
              ))}
            </div>

            <div className="border-t border-border pt-4 space-y-2 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>${cartTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Delivery</span>
                <span className="text-green-600">Digital - Instant</span>
              </div>
            </div>

            <div className="border-t border-border pt-4 mb-8">
              <div className="flex justify-between font-medium text-lg">
                <span>Total</span>
                <span data-testid="checkout-total">${cartTotal.toFixed(2)}</span>
              </div>
            </div>

            <Button
              onClick={handleCheckout}
              disabled={processing || !email}
              className="w-full rounded-none px-8 py-6 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-bold gap-2"
              data-testid="pay-btn"
            >
              <Lock className="w-4 h-4" />
              {processing ? "Processing..." : `Pay $${cartTotal.toFixed(2)}`}
            </Button>

            <p className="mt-4 text-xs text-center text-muted-foreground">
              Your payment is secure and encrypted
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
